// Lazy-init shim over @sentry/react. Keeps the SDK out of the entry chunk.
//
// main.tsx calls initSentry() from an idle callback after first paint.
// errorHandler.ts and anything else on the entry-chunk critical path should
// import captureException/setUser from here — calls made before initSentry
// resolves are buffered and replayed once the SDK is ready.

import type * as SentryReact from '@sentry/react'

type SentryModule = typeof SentryReact
type CaptureContext = Parameters<SentryModule['captureException']>[1]
type SentryUser = Parameters<SentryModule['setUser']>[0]
type InitConfig = Parameters<SentryModule['init']>[0]

let sdk: SentryModule | null = null
let initFailed = false
const pending: Array<(s: SentryModule) => void> = []

export function captureException(err: unknown, context?: CaptureContext): void {
  if (!import.meta.env.PROD) return
  if (sdk) {
    sdk.captureException(err, context)
    return
  }
  if (initFailed) return
  pending.push((s) => s.captureException(err, context))
}

export function setUser(user: SentryUser): void {
  if (!import.meta.env.PROD) return
  if (sdk) {
    sdk.setUser(user)
    return
  }
  if (initFailed) return
  pending.push((s) => s.setUser(user))
}

// Takes a factory rather than a plain config so integration constructors
// (browserTracingIntegration, replayIntegration, ...) can be built against
// the lazy-loaded SDK instance.
export async function initSentry(configFactory: (sdk: SentryModule) => InitConfig): Promise<void> {
  if (!import.meta.env.PROD) return
  if (sdk || initFailed) return
  try {
    const Sentry = await import('@sentry/react')
    Sentry.init(configFactory(Sentry))
    sdk = Sentry
    for (const fn of pending) fn(Sentry)
  } catch (err) {
    initFailed = true
    throw err
  } finally {
    pending.length = 0
  }
}
